import {
  addCallbacks,
  transform,
  useSelectedNumberByVariableKey,
  useSelectedValueAttributeByKey,
  useSelectedValueByVariableKey,
} from '@k3/core';
import { useEffect, useMemo } from 'react';
import {
  K3Discount,
  useDiscountContext,
  useDiscountPrice,
} from '../store/discountContext';
import { partition } from 'lodash';
import {
  useHeight,
  useLabelAndValue,
  useNumberLabelAndNumber,
} from './useHeight';
import { useIsEcktisch, useShopName } from './useShopName';

type Component = {
  component: string;
  number: number;
  props: string;
};

const useComponents = () => {
  const kabelwanne = useSelectedValueByVariableKey('kabelwanne');
  const kabelnetz = useSelectedValueByVariableKey('kabelnetz');

  const kabelkette = useSelectedValueByVariableKey('kabelkette');
  const kabelkettenNumber = useSelectedValueAttributeByKey(
    'kabelkette',
    'number'
  );

  const orgabox = useSelectedValueByVariableKey('orgabox');
  const orgaboxNumber = useSelectedValueAttributeByKey('orgabox', 'number');

  const kasten = useSelectedValueByVariableKey('kasten');
  const kastenPosition = useSelectedValueByVariableKey('kastenPosition');

  const kabeldurchlass = useSelectedValueByVariableKey('kabeldurchlass');
  const kabeldurchlassFarbe = useSelectedValueByVariableKey(
    'kabeldurchlassFarbe'
  );
  const kabeldurchlassNumber = useSelectedValueAttributeByKey(
    'kabeldurchlass',
    'number'
  );

  const kabelwanneEck = useSelectedValueByVariableKey('kabelwanneEck');
  const kabelwanneEckNumber = useSelectedValueAttributeByKey(
    'kabelwanneEck',
    'number'
  );

  const kabelnetzEck = useSelectedValueByVariableKey('kabelnetzEck');
  const kabelnetzEckNumber = useSelectedValueAttributeByKey(
    'kabelnetzEck',
    'number'
  );

  const kabelketteLinks = useSelectedValueByVariableKey('kabelketteLinks');
  const kabelketteLinksNumber = useSelectedValueAttributeByKey(
    'kabelketteLinks',
    'number'
  );

  const kabelketteRechts = useSelectedValueByVariableKey('kabelketteRechts');
  const kabelketteRechtsNumber = useSelectedValueAttributeByKey(
    'kabelketteRechts',
    'number'
  );

  const kabeldurchlassLinks = useSelectedValueByVariableKey(
    'kabeldurchlassLinks'
  );
  const kabeldurchlassLinksNumber = useSelectedValueAttributeByKey(
    'kabeldurchlassLinks',
    'number'
  );

  const kabeldurchlassRechts = useSelectedValueByVariableKey(
    'kabeldurchlassRechts'
  );
  const kabeldurchlassRechtsNumber = useSelectedValueAttributeByKey(
    'kabeldurchlassRechts',
    'number'
  );

  return useMemo(() => {
    const components: Component[] = [];
    if (kabelwanne && kabelwanne.value === '1')
      components.push({ component: 'Kabelwanne', number: 1, props: '' });

    if (kabelnetz && kabelnetz.value === '1')
      components.push({ component: 'Kabelnetz', number: 1, props: '' });

    if (kabelkettenNumber && kabelkettenNumber.value)
      components.push({
        component: 'Kabelkette',
        number: +kabelkettenNumber.value,
        props: kabelkette?.label ?? '',
      });

    if (orgaboxNumber && orgaboxNumber.value)
      components.push({
        component: 'Organisationsbox',
        number: +orgaboxNumber.value,
        props: orgabox?.label ?? '',
      });

    if (kasten && kasten.key !== '$noSelection')
      components.push({
        component: kasten.label,
        number: 1,
        props: kastenPosition ? `Position: ${kastenPosition.label}` : '',
      });

    if (kabeldurchlassNumber && kabeldurchlassNumber.value)
      components.push({
        component: 'Kabeldurchlass',
        number: +kabeldurchlassNumber.value,
        props: `${kabeldurchlass?.label ?? ''}, ${
          kabeldurchlassFarbe?.label ?? ''
        }`,
      });

    if (kabelwanneEck && kabelwanneEckNumber?.value)
      components.push({
        component: 'Kabelwanne',
        number: +kabelwanneEckNumber.value,
        props: kabelwanneEck?.label ?? '',
      });

    if (kabelnetzEck && kabelnetzEckNumber?.value)
      components.push({
        component: 'Kabelnetz',
        number: +kabelnetzEckNumber.value,
        props: kabelnetzEck?.label ?? '',
      });

    if (kabelketteLinks && kabelketteLinksNumber?.value)
      components.push({
        component: 'Kabelkette Linker Schenkel',
        number: +kabelketteLinksNumber.value,
        props: kabelketteLinks?.label ?? '',
      });

    if (kabelketteRechts && kabelketteRechtsNumber?.value)
      components.push({
        component: 'Kabelkette Rechter Schenkel',
        number: +kabelketteRechtsNumber.value,
        props: kabelketteRechts?.label ?? '',
      });

    if (kabeldurchlassLinks && kabeldurchlassLinksNumber?.value)
      components.push({
        component: 'Kabeldurchlass Linker Schenkel',
        number: +kabeldurchlassLinksNumber.value,
        props: kabeldurchlassLinks?.label ?? '',
      });

    if (kabeldurchlassRechts && kabeldurchlassRechtsNumber?.value)
      components.push({
        component: 'Kabeldurchlass Rechter Schenkel',
        number: +kabeldurchlassRechtsNumber.value,
        props: kabeldurchlassRechts?.label ?? '',
      });

    return components;
  }, [
    kabelwanne,
    kabelnetz,
    kabelkette,
    kabelkettenNumber,
    orgabox,
    orgaboxNumber,
    kasten,
    kastenPosition,
    kabeldurchlass,
    kabeldurchlassNumber,
    kabeldurchlassFarbe,
    kabelwanneEck,
    kabelwanneEckNumber,

    kabelnetzEck,
    kabelnetzEckNumber,
    kabelketteLinks,
    kabelketteLinksNumber,
    kabelketteRechts,
    kabelketteRechtsNumber,
    kabeldurchlassLinks,
    kabeldurchlassLinksNumber,
    kabeldurchlassRechts,
    kabeldurchlassRechtsNumber,
  ]);
};

export const useOrderCallback = () => {
  const model = useShopName();
  const depth = useSelectedNumberByVariableKey('depth');
  const height = useHeight();
  const width = useSelectedNumberByVariableKey('width');
  const dekor = useSelectedValueByVariableKey('dekor');
  const edgeDekor = useSelectedValueByVariableKey('edgeDekor');

  const plateMaterial = parseInt(
    (useSelectedValueAttributeByKey('plateMaterial', 'profiltyp')
      ?.value as string) ?? '0'
  );
  const edgeForm = useLabelAndValue('edgeForm');
  const cornerRadius = useLabelAndValue('cornerRadius');

  const legMaterial = useSelectedValueByVariableKey('legMaterial');
  const legDekor = useSelectedValueByVariableKey('legDekor');
  const gestell = useSelectedValueByVariableKey('gestell');

  const sbl = useNumberLabelAndNumber('schenkelBreiteLinks');
  const sbr = useNumberLabelAndNumber('schenkelBreiteRechts');
  const stl = useNumberLabelAndNumber('schenkelTiefeLinks');
  const str = useNumberLabelAndNumber('schenkelTiefeRechts');
  const isEcktisch = useIsEcktisch();

  const leg =
    model === 'Wangentisch'
      ? undefined
      : model.includes('Standard') && legMaterial?.key === 'wood'
      ? { label: 'Beine', value: legDekor?.label }
      : { label: 'Gestell', value: gestell?.label };
  const components = useComponents();

  const discount = useTransformedDiscount();

  useEffect(() => {
    addCallbacks({
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      saveConfiguration: (cfg) => {
        const pdfSummary = {
          model,
          depth: `${depth} cm`,
          height: height.value,
          isEcktisch,
          sbl: sbl.value,
          sbr: sbr.value,
          stl: stl.value,
          str: str.value,
          width: `${width} cm`,
          dekor: dekor?.label,
          edgeDekor: edgeDekor?.label,
          leg,
          plateMaterial: plateMaterial,
          edgeForm: edgeForm.value,
          cornerRadius: cornerRadius.value,
          components: components.map((c) => ({ ...c, number: `${c.number}x` })),
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return transform({ json: { pdfSummary, discount } })(cfg);
      },
    });
  }, [
    model,
    depth,
    height,
    width,
    dekor,
    edgeDekor,
    legMaterial,
    legDekor,
    gestell,
    leg,
    components,
    discount,
    isEcktisch,
    sbl,
    sbr,
    stl,
    str,
    plateMaterial,
    edgeForm,
    cornerRadius,
  ]);
};

const defaultDiscountValue = { discountValue: 0 };

const useTransformedDiscount = (): K3Discount | null => {
  const { discount } = useDiscountContext();
  const { discountValue } = useDiscountPrice() ?? defaultDiscountValue;

  return useMemo(() => {
    if (!discount) return null;
    const [[cDiscount], otherDiscounts] = partition(
      discount.discountInformation.discounts,
      (d) => d.discountType === 'C'
    );
    if (!cDiscount) return discount;
    const newCDiscount = { ...cDiscount, discountValue };
    return transform<K3Discount>({
      discountInformation: { discounts: [...otherDiscounts, newCDiscount] },
    })(discount);
  }, [discount, discountValue]);
};
